import React from 'react';
import { ReactComponent as Logo } from "../assets/logo/logo_white.svg";
import {
  Box,
  Container,
  Typography,
  Grid,
  Stack,
  Divider,
  List,
  ListItem,
  ListItemText,
  useTheme,
  useMediaQuery,
  IconButton,
} from '@mui/material';
import {
  LocalOffer,
  Facebook,
  Twitter,
  Instagram,
  YouTube,
  LocationOn,
  Phone,
  Email,
} from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';

const Footer = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const footerLinks = [
    {
      title: 'Product',
      items: [
        { label: 'Deals', path: '/deals' },
        { label: 'Trending', path: '/deals' },
        { label: 'Nearby Deals', path: '/deals' },
        { label: 'Categories', path: '/deals' },
      ],
    },
    {
      title: 'Company',
      items: [
        { label: 'About', path: '/about' },
        { label: 'Vendors', path: '/vendors' },
        { label: 'Contact', path: '/contact' },
        { label: 'Career', path: '/careers' },
      ],
    },
    {
      title: 'Support',
      items: [
        { label: 'Help Center', path: '/help' },
        { label: 'FAQ', path: '/faq' },
        { label: 'Terms', path: '/terms' },
        { label: 'Privacy', path: '/privacy' },
      ],
    },
  ];

  return (
    <Box
      component="footer"
      sx={{
        bgcolor: '#1a1a1a',
        color: 'grey.300',
        mt: 'auto',
      }}
    >
      {/* Main Footer */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Grid container spacing={6}>
          {/* Logo & Description */}
          <Grid item xs={12} md={4}>
            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Logo
                  style={{
                    width: "clamp(100px, 5vw, 100px)",
                    height: "clamp(100px, 5vw, 100px)",
                    flexShrink: 0,
                  }}
                />

                <Box>
                  <Typography variant="h4" fontWeight={700} color="white" sx={{ lineHeight: 1.2 }}>
                    SeekDeals
                  </Typography>
                  <Typography variant="body2" color="grey.400">
                    Seek Locals, Save Big
                  </Typography>
                </Box>
              </Box>
            </Box>
            <Typography variant="body2" sx={{ mb: 3, lineHeight: 1.7, opacity: 0.8 }}>
              Discover amazing deals from local vendors in your city. Save more, shop local, 
              live better with verified offers you can trust.
            </Typography>

            {/* Social Links */}
            <Stack direction="row" spacing={2} sx={{ mt: 4 }}>
              <IconButton
                size="small"
                component="a"
                href="http://facebook.com/profile.php?id=61584697182329"
                target="_blank"
                rel="noopener noreferrer"
                sx={{ bgcolor: 'grey.800', color: 'white', '&:hover': { bgcolor: 'grey.700' } }}
              >
                <Facebook />
              </IconButton>

              <IconButton
                size="small"
                component="a"
                href="https://x.com/DealsSeek72859"
                target="_blank"
                rel="noopener noreferrer"
                sx={{ bgcolor: 'grey.800', color: 'white', '&:hover': { bgcolor: 'grey.700' } }}
              >
                <Twitter />
              </IconButton>

              <IconButton
                size="small"
                component="a"
                href="https://www.instagram.com/seekdeals.in"
                target="_blank"
                rel="noopener noreferrer"
                sx={{ bgcolor: 'grey.800', color: 'white', '&:hover': { bgcolor: 'grey.700' } }}
              >
                <Instagram />
              </IconButton>

              <IconButton
                size="small"
                component="a"
                href="https://www.youtube.com/@seekdeals"
                target="_blank"
                rel="noopener noreferrer"
                sx={{ bgcolor: 'grey.800', color: 'white', '&:hover': { bgcolor: 'grey.700' } }}
              >
                <YouTube />
              </IconButton>
            </Stack>
          </Grid>

          {/* Links Columns */}
          {footerLinks.map((column, index) => (
            <Grid item xs={6} sm={4} md={2} key={index}>
              <Typography variant="h6" fontWeight={700} sx={{ mb: 2, color: 'white' }}>
                {column.title}
              </Typography>
              <List disablePadding>
                {column.items.map((item, idx) => (
                  <ListItem key={idx} disablePadding sx={{ py: 0.5 }}>
                    <ListItemText>
                      <Typography
                        variant="body2"
                        sx={{
                          color: 'grey.400',
                          transition: 'color 0.2s',
                          '&:hover': { color: 'white' },
                          textDecoration: 'none',
                        }}
                        component={RouterLink}
                        to={item.path}
                      >
                        {item.label}
                      </Typography>
                    </ListItemText>
                  </ListItem>
                ))}
              </List>
            </Grid>
          ))}

          {/* Contact Info */}
          <Grid item xs={12} md={4}>
            <Typography variant="h6" fontWeight={700} sx={{ mb: 2, color: 'white' }}>
              Get in Touch
            </Typography>
            <Stack spacing={2}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                <LocationOn sx={{ mt: 0.5, color: '#1a7f3a', fontSize: 20 }} />
                <Box>
                  <Typography variant="body2" fontWeight={600} color="grey.200">
                    Raipur, Chhattisgarh
                  </Typography>
                  <Typography variant="body2" color="grey.400">
                    India - 492001
                  </Typography>
                </Box>
              </Box>
              <a style={{textDecoration:"none"}} href='tel:+91 97553 75600'>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Phone sx={{ color: '#1a7f3a', fontSize: 20 }} />
                  <Typography variant="body2" color="grey.400">
                    +91 97553 75600
                  </Typography>
                </Box>
              </a>
              <a style={{textDecoration:"none"}} href='mailto:query.seekdeals@gmail.com'>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                  <Email sx={{ mt: 0.5, color: '#1a7f3a', fontSize: 20 }} />
                  <Typography variant="body2" color="grey.400">
                    query.seekdeals@gmail.com
                  </Typography>
                </Box>
              </a>
            </Stack>
          </Grid>
        </Grid>
      </Container>

      {/* Bottom Bar */}
      <Divider sx={{ bgcolor: 'grey.800' }} />
      <Container maxWidth="lg" sx={{ py: 3 }}>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { sm: 'center' }, justifyContent: 'space-between', gap: 2 }}>
          <Typography variant="body2" color="grey.500">
            © {new Date().getFullYear()} SeekDeals. All rights reserved.
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default Footer;